import React, { useContext, useState, useEffect } from 'react';
import { CourseDesignContext } from '../context/CourseDesignContext';
import { CourseIDContext } from '../context/CourseIDContext';
import './ClassOpinionOverview.css';

const ClassOpinionOverview = () => {
  const { courseDesignState } = useContext(CourseDesignContext);
  const { courseID } = useContext(CourseIDContext);
  const [allAnswers, setAllAnswers] = useState([]);
  const [selectedSectionID, setSelectedSectionID] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnswers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/studentAnswers/${courseID}`);
        if (response.ok) {
          const data = await response.json();
          setAllAnswers(data);
        }
      } catch (err) {
        console.error('Error fetching student answers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnswers();
  }, [courseID]);

  const sections = courseDesignState
    .filter(item => item.dataType === 'sectionName')
    .sort((a, b) => parseFloat(a.dataIndex) - parseFloat(b.dataIndex));

  useEffect(() => {
    if (sections.length > 0 && !selectedSectionID) {
      setSelectedSectionID(sections[0].dataID);
    }
  }, [sections, selectedSectionID]);

  const getTopicsForSection = (sectionDataID) => {
    const section = sections.find(s => s.dataID === sectionDataID);
    if (!section) return [];
    const nextSection = sections.find(s => parseFloat(s.dataIndex) > parseFloat(section.dataIndex));
    const upperBound = nextSection ? parseFloat(nextSection.dataIndex) : Infinity;

    return courseDesignState
      .filter(item => item.dataType === 'topic')
      .filter(item => {
        const idx = parseFloat(item.dataIndex);
        return idx >= parseFloat(section.dataIndex) && idx < upperBound;
      })
      .sort((a, b) => parseFloat(a.dataIndex) - parseFloat(b.dataIndex));
  };

  const getQuestionsForTopic = (topicDataID) => {
    return courseDesignState
      .filter(item =>
        item.dataType === 'question' &&
        item.questionCourseDate === topicDataID &&
        (item.questionType === 'Radio' || item.questionType === 'Checkbox')
      )
      .sort((a, b) => parseFloat(a.dataIndex) - parseFloat(b.dataIndex));
  };

  const computeDistribution = (question) => {
    const options = question.questionOptions ? question.questionOptions.split('|||') : [];
    const questionAnswers = allAnswers.filter(a => a.questionID === question.dataID && a.questionAnswer);
    const totalRespondents = questionAnswers.length;

    const counts = options.map((_, i) => {
      const val = (i + 1).toString();
      if (question.questionType === 'Radio') {
        return questionAnswers.filter(a => a.questionAnswer === val).length;
      } else {
        return questionAnswers.filter(a => (a.questionAnswer || '').split(',').includes(val)).length;
      }
    });

    return { options, counts, totalRespondents };
  };

  const renderContent = () => {
    if (!selectedSectionID) return null;
    const topics = getTopicsForSection(selectedSectionID);

    const hasAnyQuestions = topics.some(t => getQuestionsForTopic(t.dataID).length > 0);
    if (!hasAnyQuestions) {
      return <p className="opinion-overview-empty">No radio or checkbox questions in this section.</p>;
    }

    return topics.map(topic => {
      const questions = getQuestionsForTopic(topic.dataID);
      if (questions.length === 0) return null;

      return (
        <div key={topic.dataID} className="opinion-overview-topic">
          <h3 className="opinion-overview-topic-name">{topic.dataText}</h3>
          {questions.map(question => {
            const { options, counts, totalRespondents } = computeDistribution(question);
            return (
              <div key={question.dataID} className="opinion-overview-question">
                <p className="opinion-question-text">
                  {question.dataText.split('\n').map((line, i) => (
                    <React.Fragment key={i}>{line}<br /></React.Fragment>
                  ))}
                </p>
                <div className="opinion-overview-total">
                  {totalRespondents} response{totalRespondents !== 1 ? 's' : ''}
                </div>
                <div className="opinion-overview-bars">
                  {options.map((option, index) => {
                    const count = counts[index];
                    const pct = totalRespondents > 0 ? Math.round((count / totalRespondents) * 100) : 0;
                    const barWidth = pct;

                    return (
                      <div key={index} className="opinion-bar-row">
                        <div className="opinion-bar-label">{option}</div>
                        <div className="opinion-bar-track">
                          <div className="opinion-bar-fill" style={{ width: `${barWidth}%` }} />
                        </div>
                        <div className="opinion-bar-stats">{count} ({pct}%)</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      );
    });
  };

  return (
    <div className="opinion-overview-wrapper">
      <div className="opinion-overview-sidebar">
        {sections.map(section => (
          <button
            key={section.dataID}
            className={`opinion-section-button ${selectedSectionID === section.dataID ? 'active' : ''}`}
            onClick={() => setSelectedSectionID(section.dataID)}
          >
            {section.dataText}
          </button>
        ))}
      </div>
      <div className="opinion-overview-content">
        {loading ? (
          <p className="opinion-overview-loading">Loading responses…</p>
        ) : (
          renderContent()
        )}
      </div>
    </div>
  );
};

export default ClassOpinionOverview;
